/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */
import {WebGLContext, Canvas, RenderDocument} from "js_renderer";
import IScene from "../../scene_interface";
import IDocument from "../../structural_elements/document_interface";

export default
class SSAO {
    private _renderDocument: RenderDocument;
    private _sceneAPI: IScene;
    private _canvas: Canvas;
    private _sceneDocument: IDocument;
    private enabled: boolean;
    private mrt: boolean;
    private debug: number;
    private bias: number;
    private radius: number;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = this._renderDocument.getSceneAPI();
        this._canvas = this._sceneAPI.getCanvas();
        this._sceneDocument = this._sceneAPI.getDocument();

        this.enabled = false;
        this.mrt = true;
        this.debug = 0;
        this.bias = 0.009;
        this.radius = 0.10;
    }

    public setDebugBuffer(aValue: number) {
        if (aValue < 0) {
            aValue = 0;
        } else if (aValue > 4) {
            aValue = 4;
        }
        this.debug = aValue;
    }

    public setEnabled(aValue: boolean) {
        let prev = this.enabled;
        this.enabled = aValue && WebGLContext.getFeatureAvailable("FP_TEXTURES");

        // Same value as before, do nothing
        if (prev === this.enabled) {
            return;
        }

        // Recreate framebuffers
        this.invalidateFramebuffers();
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setUseMRT(aValue: boolean) {
        if (this.mrt === aValue) {
            return;
        }

        this.mrt = aValue;
        this.invalidateFramebuffers();
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setBias(aBias: number) {
        this.bias = aBias;
        if (this.bias < 0.0) {
            this.bias = 0.0;
        }
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public setRadius(aRadius: number) {
        this.radius = aRadius;
        if (this.radius < 0.0) {
            this.radius = 0.0;
        }
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public addBias(aDelta: number) {
        this.bias += aDelta;
        if (this.bias < 0.0) {
            this.bias = 0.0;
        }
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public addRadius(aDelta: number) {
        this.radius += aDelta;
        if (this.radius < 0.0) {
            this.radius = 0.0;
        }
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getDebugBuffer(): number {
        if (!this.isEnabled()) {
            return 0;
        }
        return this.debug;
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public getUseMRT(): boolean {
        return this.mrt && WebGLContext.getFeatureAvailable("DRAW_BUFFERS");
    }

    public getBias(): number {
        return this.bias;
    }

    public getRadius(): number {
        return this.radius;
    }

    /** Use this if you need to recompile shaders or recreate framebuffers */
    public invalidateFramebuffers() {
        let webglRenderGraph = this._renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.getLayer('ssao').invalidate();
        }
    }
}
